type PreviewFile = {
  id: string | number;
};

type NamedPreviewFile = PreviewFile & {
  name: string;
  isLast: boolean;
  revision: number;
  /** @deprecated поле не используется для группировки редакций */
  parentId?: number;
};

type ClickableFile = {
  isLast: boolean;
  canPreview: boolean;
  canEdit: boolean;
};

export const toFileId = (id: string | number) => String(id);

/**
 * Ключ цепочки редакций одного вложения.
 * В API редакции связаны по имени файла (как в getOldRevisionListByName).
 * PARENT_ID у вложений документа общий и для группировки не подходит.
 */
export function getAttachmentGroupKey(file: Pick<NamedPreviewFile, 'name'>) {
  return file.name;
}

export function isSameAttachment(left: Pick<NamedPreviewFile, 'name'>, right: Pick<NamedPreviewFile, 'name'>) {
  return getAttachmentGroupKey(left) === getAttachmentGroupKey(right);
}

export function isFileIconClickable(file: ClickableFile, allowIconPreview = false) {
  const canOpen = file.canPreview || (file.canEdit && file.isLast);

  return (file.isLast || allowIconPreview) && canOpen;
}

export function shouldOpenEditorOnIconClick(file: ClickableFile) {
  return file.canEdit && !file.canPreview && file.isLast;
}

export function canEditOrReviewFile(file: Pick<ClickableFile, 'isLast'>) {
  return file.isLast;
}

export function buildPreviewFiles<T extends NamedPreviewFile>(
  previewLastRevisionList: T[],
  displayedLength: number,
  openedOldRevisionIds: (string | number)[],
  allFiles: T[]
) {
  const result = previewLastRevisionList.slice(0, displayedLength);

  if (!openedOldRevisionIds.length) {
    return result;
  }

  const openedIds = new Set(openedOldRevisionIds.map(toFileId));

  return result.flatMap((file) => {
    if (!file.isLast) {
      return [file];
    }

    const oldRevisions = allFiles
      .filter((item) => !item.isLast && isSameAttachment(item, file) && openedIds.has(toFileId(item.id)))
      .sort((left, right) => right.revision - left.revision);

    return [file, ...oldRevisions];
  });
}

export function getDisplayedLength(
  id: string | number,
  list: NamedPreviewFile[],
  previewLastRevisionList: NamedPreviewFile[],
  displayedLength: number
) {
  const currentIndex = previewLastRevisionList.findIndex((file) => toFileId(file.id) === toFileId(id));

  if (currentIndex >= 0) {
    return Math.max(displayedLength, currentIndex + 1);
  }

  const oldFile = list.find((file) => toFileId(file.id) === toFileId(id));

  if (!oldFile) {
    return displayedLength;
  }

  const parentIndex = previewLastRevisionList.findIndex((file) => file.isLast && isSameAttachment(file, oldFile));

  return parentIndex >= 0 ? Math.max(displayedLength, parentIndex + 1) : displayedLength;
}

export function openOldRevision(
  id: string | number,
  list: NamedPreviewFile[],
  previewLastRevisionList: PreviewFile[],
  openedOldRevisionIds: (string | number)[]
) {
  const normalizedId = toFileId(id);

  if (
    previewLastRevisionList.some((file) => toFileId(file.id) === normalizedId) ||
    !list.some((file) => toFileId(file.id) === normalizedId)
  ) {
    return null;
  }

  if (openedOldRevisionIds.some((openedId) => toFileId(openedId) === normalizedId)) {
    return openedOldRevisionIds;
  }

  return [...openedOldRevisionIds, normalizedId];
}
